import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { signIn, signInDev, isAllowedAdmin, ALLOWED_ADMINS } from '../lib/supabase'

export default function Login() {
  const navigate = useNavigate()
  const { isAdmin } = useAuth()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  // Handle hash fragment on load
  useEffect(() => {
    const hash = window.location.hash
    if (hash && hash.includes('error')) {
      const params = new URLSearchParams(hash.substring(1))
      const error = params.get('error')
      const errorDescription = params.get('error_description')
      setMessage(errorDescription || error || 'Authentication error')
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      // In production, check admin status before attempting sign in
      if (!isDevelopment && !isAllowedAdmin(email)) {
        setMessage('This email is not authorized to access the CRM.')
        setLoading(false)
        return
      }

      if (isDevelopment) {
        await signInDev(email)
        // Admin check will happen automatically via AuthContext after sign in
      } else {
        await signIn(email)
        setMessage('Check your email for the magic link!')
      }
    } catch (error: any) {
      console.error('Login error:', error)
      setMessage(error.message || 'An error occurred during sign in')
    } finally {
      setLoading(false)
    }
  }

  // Redirect if already authenticated
  if (isAdmin) {
    navigate('/clients')
    return null
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to PredictHire CRM
          </h2>
          {isDevelopment && (
            <p className="mt-2 text-center text-sm text-gray-600">
              Development Mode - Direct Sign In Enabled
            </p>
          )}
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {message && (
            <div className={`text-sm text-center ${message.includes('Check your email') ? 'text-green-600' : 'text-red-600'}`}>
              {message}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Signing in...' : isDevelopment ? 'Sign In' : 'Send Magic Link'}
            </button>
          </div>
        </form>

        {/* Development-only quick login section */}
        {isDevelopment && (
          <div className="mt-8">
            <div className="text-sm text-center text-gray-600 mb-4">
              Quick Login
            </div>
            <div className="space-y-2">
              {ALLOWED_ADMINS.map(adminEmail => (
                <button
                  key={adminEmail}
                  onClick={async () => {
                    setEmail(adminEmail)
                    setLoading(true)
                    try {
                      await signInDev(adminEmail)
                    } catch (error: any) {
                      console.error('Quick login error:', error)
                      setMessage(error.message)
                    } finally {
                      setLoading(false)
                    }
                  }}
                  className="w-full px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={loading}
                >
                  Login as {adminEmail}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 